import './instrument';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ThemeProvider } from 'next-themes';
import posthog from 'posthog-js';
import { PostHogProvider } from '@posthog/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import './app.css';
import { App } from './app';
import { captureFirstTouch } from './lib/first-touch';
import { SessionProvider } from './contexts/session-context';
import { initFirstPromoter } from './lib/firstpromoter';
import { makeQueryClient } from './lib/query/query-client';
import { isCardLinkPath } from './lib/strip-card-token';

const queryClient = makeQueryClient();

// Augment Window so Playwright page tests can observe posthog.on('eventCaptured')
// without a double-cast (main.tsx sets this; tests read it).
declare global {
  interface Window {
    posthog?: typeof posthog;
  }
}

// Phase quick-260426-x4h Task B — IS_LOCAL_DEV gate. Local docker stack
// disables PostHog so dev sessions don't pollute the funnel.
if (
  import.meta.env.VITE_IS_LOCAL_DEV !== '1' &&
  import.meta.env.VITE_PUBLIC_POSTHOG_TOKEN
) {
  // Cookie name intentionally NOT set: posthog-js's default
  // `ph_<token>_posthog` is identical between web/ and frontend/ when both
  // call init with the same project token, so the .hookmyapp.com cookie is
  // shared automatically. Customizing cookie_name here previously
  // double-prefixed to `ph_ph_hookmyapp` and orphaned the marketing-site
  // identity (Phase 113-03 bug discovered 2026-05-10 via langtalks1 cohort).
  posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_TOKEN, {
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
    ui_host: 'https://us.posthog.com',
    defaults: '2026-01-30',
    person_profiles: 'always',
    persistence: 'localStorage+cookie',
    capture_performance: { web_vitals: true },
    // AIT-505 — the public /card add-card link carries its one-time token in
    // the URL fragment (#crd_...). PostHog's autocapture records
    // window.location.href (fragment included) for $pageview / route-change
    // events; this native option drops the fragment before capture. The page
    // itself also scrubs the fragment via history.replaceState on mount —
    // this is defense in depth, not the only guard.
    disable_capture_url_hashes: true,
  });
  // Phase 113 — tag every event so staging/prod/local + marketing/app are
  // distinguishable in the shared PostHog project (CONTEXT.md §PostHog project architecture).
  // VITE_SENTRY_ENVIRONMENT is reused (set by deploy-staging/production.yml build-args).
  posthog.register({
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? 'local',
    site: 'app',
  });
  // Phase 125-06 — first-touch click-ID + UTM capture. Runs AFTER init + the
  // site/env register baseline so the click-IDs join the super-property set
  // before any React-level capture fires. Fails open on JSON.parse /
  // localStorage errors per CLAUDE.md (observability never blocks UI).
  captureFirstTouch();
  // Mirror posthog's snippet-loader behavior so DevTools + Playwright tests
  // can observe captures via `posthog.on('eventCaptured', ...)`. The ESM
  // import doesn't auto-attach the singleton; doing it here matches the
  // `<script>`-loaded SDK convention and is the tested observation point
  // for `*.page.test.ts` PostHog assertions.
  window.posthog = posthog;
}

// AIT-505 — never load the FirstPromoter script on the public /card add-card
// page: the permanent bearer token sits in `location.hash` there, and fpr.js
// is a third-party script with no reason to run on that page. Mirrors the
// Session Replay gate in ./lib/sentry. Codex round 6 (P1) — `isCardLinkPath`
// normalizes trailing slashes so `/card/` (same page, different URL) still
// gates the same as `/card`.
if (!isCardLinkPath(window.location.pathname)) {
  initFirstPromoter();
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <PostHogProvider client={posthog}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider attribute="class" defaultTheme="dark" disableTransitionOnChange>
          <SessionProvider>
            <App />
          </SessionProvider>
        </ThemeProvider>
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </PostHogProvider>
  </StrictMode>,
);
